import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tabs,
  Tab
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import AddCouponDialog from 'components/admin/AddCouponDialog';
import MainCard from 'components/MainCard';
import { getCoupon } from 'services/coupon/getCoupon';
import { deActivate } from 'services/coupon/deActivate';

function CouponsList() {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [tabIndex, setTabIndex] = useState(0); // 0 = All, 1 = Active, 2 = Inactive
  const queryClient = useQueryClient();

  const {
    data: couponsData,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['allcoupons'],
    queryFn: getCoupon
  });

  const couponList = Array.isArray(couponsData) ? couponsData : [];

  // Tab Change Handler
  const handleTabChange = (event, newValue) => {
    setTabIndex(newValue);
  };

  // Counts for tabs
  const allCount = couponList.length;
  const activeCount = couponList.filter((c) => c.isactive === 1).length;
  const inactiveCount = couponList.filter((c) => c.isactive !== 1).length;

  // Filtered list based on selected tab
  const filteredCoupons = couponList.filter((coupon) => {
    if (tabIndex === 1) return coupon.isactive === 1;
    if (tabIndex === 2) return coupon.isactive !== 1;
    return true;
  });

  const handleOpenAddDialog = () => setAddDialogOpen(true);
  const handleCloseAddDialog = () => setAddDialogOpen(false);

  const { mutate, isLoading: isDeactivating } = useMutation({
    mutationFn: deActivate,
    onSuccess: (data) => {
      toast.success(data?.message || 'Status updated');
      queryClient.invalidateQueries(['allcoupons']);
    },
    onError: (error) => {
      toast.error('Failed to update status: ' + (error.message || 'Unknown error'));
    }
  });

  const handleModifyStatus = (couponId, isActive) => {
    mutate({
      couponid: couponId,
      key: isActive === 1 ? 2 : 1
    });
  };

  return (
    <MainCard>
      {/* Tabs and Add Button */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Tabs value={tabIndex} onChange={handleTabChange}>
          <Tab label="All" icon={<Chip label={allCount} color="primary" size="small" />} iconPosition="end" />
          <Tab label="Active" icon={<Chip label={activeCount} color="success" size="small" />} iconPosition="end" />
          <Tab label="Inactive" icon={<Chip label={inactiveCount} color="error" size="small" />} iconPosition="end" />
        </Tabs>
        <Button variant="contained" color="primary" onClick={handleOpenAddDialog}>
          Create Coupon
        </Button>
      </Box>

      {/* Loading & Error States */}
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <CircularProgress />
        </Box>
      ) : isError ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
          <Typography color="error">{error?.message || 'Failed to load coupons.'}</Typography>
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 800 }} aria-label="coupon table">
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Coupon Code</TableCell>
                <TableCell>Amount (₹)</TableCell>
                <TableCell>Max Uses</TableCell>
                <TableCell>Used Count</TableCell>
                <TableCell>Expire Date</TableCell>
                <TableCell>Remark</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCoupons.length > 0 ? (
                filteredCoupons.map((code, index) => (
                  <TableRow key={code.id}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{code.code?.toUpperCase()}</TableCell>
                    <TableCell>{code.discountvalue}</TableCell>
                    <TableCell>{code.maxuses}</TableCell>
                    <TableCell>{code.uses}</TableCell>
                    <TableCell>{code.expiresat?.split('T')[0]}</TableCell>
                    <TableCell>{code.remark}</TableCell>
                    <TableCell>
                      <Chip label={code.isactive === 1 ? 'Active' : 'Inactive'} color={code.isactive === 1 ? 'success' : 'error'} />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={code.isactive === 1}
                        color="success"
                        onChange={() => handleModifyStatus(code.id, code.isactive)}
                        disabled={isDeactivating}
                      />
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center">
                    <Box sx={{ mt: 2 }}>No Coupons Found.</Box>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <AddCouponDialog open={addDialogOpen} onClose={handleCloseAddDialog} />
    </MainCard>
  );
}

export default CouponsList;
